import React, { useEffect, useState, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../AuthContext'
import { API_BASE } from '../config'

const C = {
  g900:'#0D2218', g800:'#1A3A28', g700:'#235233', g600:'#2D6B42',
  g500:'#3D8A57', g400:'#5AAE73', g300:'#85C99A', g100:'#E8F5EE', g50:'#F2FAF5',
  white:'#FFFFFF', text:'#1A2820', muted:'#5A7065', border:'#C8DDD2',
  error:'#C0392B', errorBg:'#FEF2F2', errorBorder:'#FECACA',
  headerGrad:'linear-gradient(150deg,#0F3D24 0%,#1E6B3C 60%,#2E8B4F 100%)',
}
const STATUS_COLORS = {
  pending:   { bg:'#FEF9F0', color:'#B7770D', border:'#FCEDC7' },
  confirmed: { bg:'#EBF8F0', color:'#1A6B3C', border:'#A3DFB8' },
  completed: { bg:'#EDF2FF', color:'#3451B2', border:'#BAC8FF' },
  cancelled: { bg:'#FFF0F0', color:'#C92A2A', border:'#FFC9C9' },
}

const pad = n => String(n).padStart(2,'0')
const todayISO = () => { const d=new Date(); return `${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())}` }
const fmtDate = d => {
  if (!d) return '—'
  try {
    const s = typeof d==='string'?(d.includes('T')?d.split('T')[0]:d.split(' ')[0]):new Date(d).toISOString().slice(0,10)
    const [y,m,day] = s.split('-'); return `${day}/${m}/${y}`
  } catch { return String(d) }
}
const fmtRelative = d => {
  if (!d) return '—'
  try {
    const date = new Date(d.includes('T')?d:d+'T00:00:00')
    const diff = Math.floor((Date.now()-date)/86400000)
    if (diff===0) return 'Today'; if (diff===1) return 'Yesterday'
    if (diff<7) return `${diff}d ago`; if (diff<30) return `${Math.floor(diff/7)}w ago`
    return fmtDate(d)
  } catch { return fmtDate(d) }
}
const fmtTime = iso => {
  if (!iso) return '—'
  try { return new Date(iso).toLocaleString('en-IN',{day:'2-digit',month:'short',hour:'2-digit',minute:'2-digit'}) }
  catch { return iso }
}

// ─── Quick Reminder — one-click, no modal ─────────────────────────────────────
function QuickRemind({ patient, appt, fetchWithAuth, onDone }) {
  const [sending, setSending] = useState(null)
  const [sent, setSent]       = useState(null)

  const send = async (type) => {
    const messages = {
      medication: `Hi ${patient.name}, this is a reminder to take your medication as prescribed. Stay on track with your treatment! — Revive Skin & Hair`,
      missed:     `Hi ${patient.name}, we noticed you missed some medication doses. Please take them as soon as possible and continue your treatment. — Revive Skin & Hair`,
      followup:   `Hi ${patient.name}, your follow-up appointment is due soon. Please book your next consultation at reviveskin.in — Revive Team`,
    }
    setSending(type)
    try {
      const res = await fetchWithAuth(`${API_BASE}/api/reminders/send`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ appointmentId: appt?.id, email: patient.email, phone: patient.phone, name: patient.name, message: messages[type] }),
      })
      const data = await res.json()
      if (!res.ok) throw new Error(data.message || 'Send failed')
      setSent(type)
      setTimeout(() => { setSent(null); onDone && onDone() }, 2000)
    } catch(e) {
      alert('Failed to send reminder: ' + e.message)
    }
    finally { setSending(null) }
  }

  const btns = [
    { key:'medication', label:'💊 Medication', color: C.g700 },
    { key:'missed',     label:'❌ Missed Dose', color: '#C0392B' },
    { key:'followup',   label:'📅 Follow-up',   color: '#3451B2' },
  ]

  return (
    <div style={{ display:'flex', gap:6, flexWrap:'wrap' }}>
      {btns.map(b => (
        <button key={b.key} onClick={() => send(b.key)} disabled={!!sending||!!sent}
          style={{ padding:'.3rem .75rem', borderRadius:7, background: sent===b.key ? C.g500 : b.color, color:'white', border:'none', fontSize:'.7rem', fontWeight:600, cursor:'pointer', fontFamily:"'DM Sans',sans-serif", opacity: sending&&sending!==b.key ? .5 : 1, transition:'all .2s' }}>
          {sending===b.key ? '⏳ Sending…' : sent===b.key ? '✅ Sent!' : b.label}
        </button>
      ))}
    </div>
  )
}

// ─── Adherence Calendar ───────────────────────────────────────────────────────
function AdherenceCalendar({ calendarData, selectedDate, onSelectDate }) {
  const today = todayISO()
  const [filterPatient, setFilterPatient] = React.useState('all')

  // Get unique patients from calendarData snapshots
  const patientList = useMemo(() => {
    const map = {}
    calendarData.forEach(d => {
      (d.patients||[]).forEach(p => { if(!map[p.email]) map[p.email] = p.name })
    })
    return Object.entries(map).map(([email,name]) => ({ email, name }))
  }, [calendarData])

  // Filter calendarData per selected patient
  const filteredData = useMemo(() => {
    if (filterPatient === 'all') return calendarData
    return calendarData.map(d => {
      const pt = (d.patients||[]).find(p => p.email === filterPatient)
      if (!pt) return { ...d, total:0, taken:0, missed:0, pending:0, patientCount:0 }
      return { ...d, total:pt.total, taken:pt.taken, missed:pt.missed, pending:pt.pending, patientCount:1 }
    })
  }, [calendarData, filterPatient])

  // Build full 30-day grid
  const days = useMemo(() => {
    const result = []
    for (let i = 29; i >= 0; i--) {
      const d = new Date(); d.setDate(d.getDate() - i)
      const iso = `${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())}`
      const data = filteredData.find(c => c.date === iso) || null
      result.push({ iso, data, dayOfWeek: d.getDay() })
    }
    return result
  }, [filteredData])

  const getColor = (day) => {
    if (!day.data || day.data.total === 0) return { bg:'#F3F4F6', border:C.border, dot:C.muted }
    if (day.data.missed > 0) return { bg:'#FEF2F2', border:'#FECACA', dot:C.error }
    if (day.data.taken === day.data.total) return { bg:'#F0FFF4', border:C.g300, dot:C.g500 }
    return { bg:'#FEF9F0', border:'#FCEDC7', dot:'#B7770D' }
  }

  return (
    <div>
      {/* Header + Patient filter + Legend */}
      <div style={{ display:'flex', gap:'.5rem', alignItems:'center', marginBottom:'.75rem', flexWrap:'wrap' }}>
        <div style={{ fontSize:'.65rem', fontWeight:700, color:C.muted, textTransform:'uppercase', letterSpacing:'.08em' }}>30-Day Adherence Calendar</div>

        {/* Patient filter */}
        <select value={filterPatient} onChange={e => setFilterPatient(e.target.value)}
          style={{ marginLeft:'.5rem', padding:'.25rem .6rem', borderRadius:7, border:`1px solid ${C.border}`, fontSize:'.72rem', color:C.text, background:'white', cursor:'pointer', fontFamily:"'DM Sans',sans-serif", outline:'none' }}>
          <option value="all">👥 All Patients</option>
          {patientList.map(p => (
            <option key={p.email} value={p.email}>👤 {p.name}</option>
          ))}
        </select>

        <div style={{ display:'flex', gap:'.4rem', marginLeft:'auto', flexWrap:'wrap' }}>
          {[['#F0FFF4','#85C99A','All Taken'],['#FEF2F2','#FECACA','Missed'],['#FEF9F0','#FCEDC7','Partial'],['#F3F4F6',C.border,'No Data']].map(([bg,border,label]) => (
            <div key={label} style={{ display:'flex', alignItems:'center', gap:3, fontSize:'.6rem', color:C.muted }}>
              <div style={{ width:10, height:10, borderRadius:2, background:bg, border:`1px solid ${border}` }} />
              {label}
            </div>
          ))}
        </div>
      </div>

      {/* Day of week headers */}
      <div style={{ display:'grid', gridTemplateColumns:'repeat(7,1fr)', gap:3, marginBottom:3 }}>
        {['Sun','Mon','Tue','Wed','Thu','Fri','Sat'].map(d => (
          <div key={d} style={{ textAlign:'center', fontSize:'.55rem', color:C.muted, fontWeight:600 }}>{d}</div>
        ))}
      </div>

      {/* Calendar grid */}
      <div style={{ display:'grid', gridTemplateColumns:'repeat(7,1fr)', gap:2 }}>
        {Array.from({ length: days[0].dayOfWeek }).map((_, i) => <div key={`e${i}`} style={{ height:40 }} />)}
        {days.map(day => {
          const colors = getColor(day)
          const isSelected = day.iso === selectedDate
          const isToday = day.iso === today
          const [,mm,dd] = day.iso.split('-')
          return (
            <button key={day.iso} onClick={() => onSelectDate(day.iso === selectedDate ? null : day.iso)}
              style={{ height:40, borderRadius:6, background:isSelected ? C.g700 : colors.bg, border:`1.5px solid ${isSelected ? C.g500 : isToday ? C.g400 : colors.border}`, cursor:'pointer', padding:'2px 0', display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', gap:1, transition:'all .15s', position:'relative' }}>
              <div style={{ fontSize:'.65rem', fontWeight: isToday ? 700 : 400, color: isSelected ? 'white' : isToday ? C.g700 : C.text }}>{dd}</div>
              {day.data && day.data.total > 0 && (
                <div style={{ width:4, height:4, borderRadius:'50%', background: isSelected ? 'rgba(255,255,255,.7)' : colors.dot }} />
              )}
              {day.data && day.data.missed > 0 && !isSelected && (
                <div style={{ position:'absolute', top:1, right:3, fontSize:'.45rem', color:C.error, fontWeight:700 }}>{day.data.missed}</div>
              )}
            </button>
          )
        })}
      </div>

      {/* Selected date detail */}
      {selectedDate && (() => {
        const dayData = filteredData.find(c => c.date === selectedDate)
        const [y,m,d] = selectedDate.split('-')
        const isToday = selectedDate === today
        const selectedPatientData = filterPatient !== 'all' && dayData
          ? (calendarData.find(c=>c.date===selectedDate)?.patients||[]).find(p=>p.email===filterPatient)
          : null
        return (
          <div style={{ marginTop:'.85rem', padding:'.85rem 1rem', background:C.g50, border:`1px solid ${C.border}`, borderRadius:10 }}>
            <div style={{ fontWeight:700, color:C.g900, fontSize:'.82rem', marginBottom:'.5rem', display:'flex', alignItems:'center', justifyContent:'space-between' }}>
              <span>📅 {d}/{m}/{y} {isToday ? '(Today)' : ''}</span>
              {filterPatient !== 'all' && <span style={{ fontSize:'.68rem', background:C.g100, color:C.g700, padding:'.15rem .5rem', borderRadius:5, fontWeight:600 }}>👤 {patientList.find(p=>p.email===filterPatient)?.name}</span>}
            </div>
            {isToday ? (
              <div style={{ fontSize:'.78rem', color:C.muted }}>⏳ Today's results will be available tomorrow after midnight. Patient data is being tracked.</div>
            ) : !dayData || dayData.total === 0 ? (
              <div style={{ fontSize:'.78rem', color:C.muted }}>No medication data for this date.</div>
            ) : (
              <>
                <div style={{ display:'flex', gap:'1rem', flexWrap:'wrap', marginBottom: selectedPatientData?.medications?.length ? '.75rem' : 0 }}>
                  {[
                    ['Total Doses', dayData.total, C.muted],
                    ['✅ Taken', dayData.taken, C.g600],
                    ['❌ Missed', dayData.missed, C.error],
                    ['⏳ Pending', dayData.pending, '#B7770D'],
                    filterPatient === 'all' ? ['👥 Patients', dayData.patientCount, C.muted] : null,
                  ].filter(Boolean).map(([label, val, color]) => (
                    <div key={label}>
                      <div style={{ fontSize:'.6rem', color:C.muted, textTransform:'uppercase', letterSpacing:'.07em' }}>{label}</div>
                      <div style={{ fontSize:'1.1rem', fontWeight:700, color }}>{val}</div>
                    </div>
                  ))}
                </div>

                {/* Per-patient breakdown when All is selected */}
                {filterPatient === 'all' && (calendarData.find(c=>c.date===selectedDate)?.patients||[]).length > 0 && (
                  <div style={{ display:'flex', flexDirection:'column', gap:4 }}>
                    <div style={{ fontSize:'.62rem', color:C.muted, fontWeight:700, textTransform:'uppercase', letterSpacing:'.07em', marginBottom:2 }}>Per Patient</div>
                    {(calendarData.find(c=>c.date===selectedDate)?.patients||[]).map(p => (
                      <div key={p.email} style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'.4rem .65rem', background:'white', borderRadius:7, border:`1px solid ${p.missed>0?'#FECACA':C.border}` }}>
                        <div>
                          <div style={{ fontSize:'.75rem', fontWeight:600, color:C.g900 }}>{p.name}</div>
                          <div style={{ fontSize:'.62rem', color:C.muted }}>{p.email}</div>
                        </div>
                        <div style={{ display:'flex', gap:8 }}>
                          <span style={{ fontSize:'.68rem', color:C.g600, fontWeight:600 }}>✅ {p.taken}</span>
                          {p.missed>0&&<span style={{ fontSize:'.68rem', color:C.error, fontWeight:600 }}>❌ {p.missed}</span>}
                          {p.pending>0&&<span style={{ fontSize:'.68rem', color:'#B7770D', fontWeight:600 }}>⏳ {p.pending}</span>}
                          <span style={{ fontSize:'.62rem', color:C.muted }}>/ {p.total}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* Individual medication breakdown */}
                {selectedPatientData?.medications?.length > 0 && (
                  <div style={{ display:'flex', flexDirection:'column', gap:4 }}>
                    <div style={{ fontSize:'.62rem', color:C.muted, fontWeight:700, textTransform:'uppercase', letterSpacing:'.07em', marginBottom:2 }}>Medication Detail</div>
                    {selectedPatientData.medications.map((med,i) => (
                      <div key={i} style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'.35rem .65rem', background:'white', borderRadius:7, border:`1px solid ${med.status==='missed'?'#FECACA':med.taken?C.g300:C.border}` }}>
                        <div style={{ fontSize:'.75rem', color:C.text }}>{med.type==='morning'?'🌅':med.type==='afternoon'?'☀️':'🌙'} {med.name}</div>
                        <span style={{ fontSize:'.68rem', fontWeight:600, color: med.taken?C.g600:med.status==='missed'?C.error:'#B7770D' }}>
                          {med.taken?'✅ Taken':med.status==='missed'?'❌ Missed':'⏳ Pending'}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}
          </div>
        )
      })()}
    </div>
  )
}

// ─── Main Admin Dashboard ─────────────────────────────────────────────────────
export default function AdminDashboard() {
  const { user, fetchWithAuth } = useAuth()
  const navigate = useNavigate()

  const [allAppointments, setAllAppointments] = useState([])
  const [reminderLogs, setReminderLogs]        = useState([])
  const [missedReminders, setMissedReminders]  = useState([])
  const [calendarData, setCalendarData]        = useState([])
  const [loading, setLoading]                  = useState(true)
  const [search, setSearch]                    = useState('')
  const [statusFilter, setStatusFilter]        = useState('all')
  const [selectedPatient, setSelectedPatient]  = useState(null)
  const [activeTab, setActiveTab]              = useState('overview')
  const [mainTab, setMainTab]                  = useState(() => {
    const h = (typeof window !== 'undefined' ? window.location.hash : '').replace('#', '')
    return ['patients', 'calendar', 'reminders', 'voice', 'reviews', 'staff', 'pharmacies'].includes(h) ? h : 'patients'
  })
  const [showAlerts, setShowAlerts]            = useState(true)
  const [selectedCalDate, setSelectedCalDate]  = useState(null)
  const [notifications, setNotifications]      = useState([])
  const [notifUnread, setNotifUnread]          = useState(0)
  const [notifOpen, setNotifOpen]              = useState(false)
  const [patientWaPref, setPatientWaPref]      = useState(null)
  const [waToggling, setWaToggling]            = useState(false)

  useEffect(() => {
    if (!user?.token) { navigate('/admin/login'); return }
    loadAll()
  }, [])

  useEffect(() => {
    if (!selectedPatient?.email) { setPatientWaPref(null); return }
    fetchWithAuth(`${API_BASE}/api/admin/patient-wa-pref/${encodeURIComponent(selectedPatient.email)}`)
      .then(r => r.ok ? r.json() : null)
      .then(d => { if (d) setPatientWaPref(d) })
      .catch(() => {})
  }, [selectedPatient?.email])

  const loadAll = async () => {
    setLoading(true)
    try {
      const res = await fetchWithAuth(`${API_BASE}/api/followups`)
      if (!res.ok) throw new Error()
      const j = await res.json()
      setAllAppointments(j.followups || [])

      try {
        const rRes = await fetchWithAuth(`${API_BASE}/api/reminders/logs`)
        if (rRes.ok) { const rj = await rRes.json(); setReminderLogs(rj.logs || []) }
      } catch {}

      try {
        const mRes = await fetchWithAuth(`${API_BASE}/api/reminders/missed-today`)
        if (mRes.ok) { const mj = await mRes.json(); setMissedReminders(mj.missed || []) }
      } catch {}

      try {
        const cRes = await fetchWithAuth(`${API_BASE}/api/reminders/daily-adherence`)
        if (cRes.ok) { const cj = await cRes.json(); setCalendarData(cj.adherence || []) }
      } catch {}

      try {
        const nRes = await fetchWithAuth(`${API_BASE}/api/admin/notifications`)
        if (nRes.ok) { const nj = await nRes.json(); setNotifications(nj.notifications||[]); setNotifUnread(nj.unread||0) }
      } catch {}

    } catch { setAllAppointments([]) }
    finally { setLoading(false) }
  }

  const markNotifRead = async (id) => {
    await fetchWithAuth(`${API_BASE}/api/admin/notifications/${id}/read`, { method: 'PUT' })
    setNotifications(prev => prev.map(n => n.id === id ? { ...n, read: 1 } : n))
    setNotifUnread(prev => Math.max(0, prev - 1))
  }

  const markAllRead = async () => {
    await fetchWithAuth(`${API_BASE}/api/admin/notifications/read-all`, { method: 'PUT' })
    setNotifications(prev => prev.map(n => ({ ...n, read: 1 })))
    setNotifUnread(0)
  }

  const notifIcon = type => type === 'prescription_added' ? '💊' : type === 'product_deleted' ? '🗑️' : type === 'missing_image' ? '🖼️' : '🔔'
  const fmtNotifTime = iso => {
    try {
      const diff = Math.floor((Date.now() - new Date(iso)) / 60000)
      if (diff < 1) return 'just now'
      if (diff < 60) return `${diff}m ago`
      if (diff < 1440) return `${Math.floor(diff/60)}h ago`
      return `${Math.floor(diff/1440)}d ago`
    } catch { return '' }
  }

  const patients = useMemo(() => {
    const map = {}
    for (const appt of allAppointments) {
      const key = appt.email||appt.name||'Unknown'
      if (!map[key]) map[key] = { email:appt.email, name:appt.name, phone:appt.phone, appointments:[] }
      map[key].appointments.push(appt)
    }
    for (const p of Object.values(map)) p.appointments.sort((a,b)=>(b.date||'').localeCompare(a.date||''))
    return Object.values(map).sort((a,b)=>b.appointments.length-a.appointments.length)
  }, [allAppointments])

  const filtered = useMemo(() => patients.filter(p => {
    const q = search.toLowerCase()
    const matchSearch = !q||p.name?.toLowerCase().includes(q)||p.email?.toLowerCase().includes(q)||p.phone?.includes(q)
    const matchStatus = statusFilter==='all'||p.appointments.some(a=>a.status===statusFilter)
    return matchSearch && matchStatus
  }), [patients, search, statusFilter])

  const stats = useMemo(() => ({
    totalPatients: patients.length,
    totalAppts:    allAppointments.length,
    pending:       allAppointments.filter(a=>a.status==='pending').length,
    onMedication:  patients.filter(p=>p.appointments.some(a=>a.medication||a.prescription)).length,
    missedToday:   missedReminders.length,
    remindersSent: reminderLogs.length,
  }), [patients, allAppointments, missedReminders, reminderLogs])

  const missedByPatient = useMemo(() => {
    const map = {}
    for (const m of missedReminders) {
      const key = m.patientEmail||'unknown'
      if (!map[key]) map[key] = { email:m.patientEmail, missed:[] }
      map[key].missed.push(m)
    }
    return Object.values(map)
  }, [missedReminders])

  if (!user?.token) return null

  return (
    <div style={{ minHeight:'100vh', background:C.g50, fontFamily:"'DM Sans',sans-serif" }}>

      {/* HEADER */}
      <div style={{ background:C.headerGrad, padding:'1.1rem 2rem', position:'relative', overflow:'hidden' }}>
        <div style={{ position:'absolute', inset:0, backgroundImage:`radial-gradient(circle,rgba(255,255,255,.02) 1px,transparent 1px)`, backgroundSize:'24px 24px', pointerEvents:'none' }} />
        <div style={{ maxWidth:1300, margin:'0 auto', position:'relative', zIndex:1, display:'flex', justifyContent:'space-between', alignItems:'center', gap:'1rem', flexWrap:'wrap' }}>
          <div>
            <div style={{ fontSize:'.58rem', letterSpacing:'.18em', textTransform:'uppercase', color:C.g300, opacity:.8, marginBottom:'.15rem' }}>Revive · Admin</div>
            <h1 style={{ fontFamily:"'DM Serif Display',serif", fontSize:'1.35rem', color:'white', margin:0 }}>Patient Progress Tracker</h1>
          </div>
          <div style={{ display:'flex', gap:'.5rem', flexWrap:'wrap' }}>
            {[
              ['👥', stats.totalPatients, 'Patients', false],
              ['📋', stats.totalAppts,    'Appointments', false],
              ['⏳', stats.pending,       'Pending', false],
              ['💊', stats.onMedication,  'On Medication', false],
              ['❌', stats.missedToday,   'Missed Today', stats.missedToday>0],
              ['📤', stats.remindersSent, 'Reminders Sent', false],
            ].map(([icon,val,label,alert]) => (
              <div key={label} style={{ background:alert?'rgba(192,57,43,.25)':'rgba(255,255,255,.08)', border:`1px solid ${alert?'rgba(192,57,43,.4)':'rgba(255,255,255,.1)'}`, borderRadius:9, padding:'.4rem .8rem', display:'flex', alignItems:'center', gap:6 }}>
                <span style={{ fontSize:'.85rem' }}>{icon}</span>
                <div>
                  <div style={{ fontFamily:"'DM Serif Display',serif", fontSize:'1rem', color:alert?'#FF9999':'white', lineHeight:1 }}>{val}</div>
                  <div style={{ fontSize:'.52rem', color:'rgba(255,255,255,.38)', textTransform:'uppercase', letterSpacing:'.07em', marginTop:1 }}>{label}</div>
                </div>
              </div>
            ))}
          </div>
          <div style={{ display:'flex', gap:'.5rem', alignItems:'center', position:'relative' }}>
            {/* Bell notification button */}
            <button
              onClick={() => setNotifOpen(o => !o)}
              style={{ position:'relative', padding:'.45rem .7rem', borderRadius:8, background: notifUnread > 0 ? 'rgba(220,38,38,.25)' : 'rgba(255,255,255,.1)', border:`1px solid ${notifUnread > 0 ? 'rgba(220,38,38,.5)' : 'rgba(255,255,255,.15)'}`, color:'white', fontSize:'1rem', cursor:'pointer', lineHeight:1 }}
            >
              🔔
              {notifUnread > 0 && (
                <span style={{ position:'absolute', top:-6, right:-6, background:'#dc2626', color:'white', borderRadius:999, fontSize:'.58rem', fontWeight:700, padding:'2px 5px', minWidth:16, textAlign:'center', lineHeight:1.4 }}>
                  {notifUnread > 99 ? '99+' : notifUnread}
                </span>
              )}
            </button>

            <button onClick={loadAll} style={{ padding:'.45rem 1rem', borderRadius:8, background:'rgba(255,255,255,.1)', border:'1px solid rgba(255,255,255,.15)', color:'white', fontSize:'.78rem', cursor:'pointer', fontFamily:"'DM Sans',sans-serif" }}>🔄 Refresh</button>

            {/* Notification dropdown panel */}
            {notifOpen && (
              <>
                <div onClick={() => setNotifOpen(false)} style={{ position:'fixed', inset:0, zIndex:999 }} />
                <div style={{ position:'absolute', top:'calc(100% + 8px)', right:0, width:380, maxHeight:480, overflowY:'auto', background:'white', borderRadius:12, boxShadow:'0 8px 32px rgba(0,0,0,.18)', border:'1px solid #e5e7eb', zIndex:1000 }}>
                  <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', padding:'12px 16px', borderBottom:'1px solid #f3f4f6', position:'sticky', top:0, background:'white' }}>
                    <span style={{ fontWeight:700, fontSize:'.88rem', color:'#1a2820' }}>Notifications {notifUnread > 0 && <span style={{ background:'#dc2626', color:'white', borderRadius:999, fontSize:'.65rem', padding:'2px 7px', marginLeft:4 }}>{notifUnread}</span>}</span>
                    {notifUnread > 0 && (
                      <button onClick={markAllRead} style={{ fontSize:'.72rem', color:C.g600, background:'none', border:'none', cursor:'pointer', fontWeight:600 }}>Mark all read</button>
                    )}
                  </div>
                  {notifications.length === 0 ? (
                    <div style={{ padding:'2rem', textAlign:'center', color:'#9ca3af', fontSize:'.82rem' }}>No notifications yet</div>
                  ) : notifications.map(n => (
                    <div
                      key={n.id}
                      onClick={() => { if (!n.read) markNotifRead(n.id) }}
                      style={{ display:'flex', gap:10, padding:'12px 16px', borderBottom:'1px solid #f9fafb', cursor: n.read ? 'default' : 'pointer', background: n.read ? 'white' : '#f0fdf4', transition:'background .15s' }}
                    >
                      <div style={{ fontSize:'1.1rem', flexShrink:0, marginTop:2 }}>{notifIcon(n.type)}</div>
                      <div style={{ flex:1, minWidth:0 }}>
                        <div style={{ fontSize:'.82rem', fontWeight: n.read ? 500 : 700, color:'#1a2820', marginBottom:2 }}>{n.title}</div>
                        {n.body && <div style={{ fontSize:'.74rem', color:'#5a7065', lineHeight:1.4 }}>{n.body}</div>}
                        <div style={{ fontSize:'.68rem', color:'#9ca3af', marginTop:3 }}>{fmtNotifTime(n.createdAt)}</div>
                      </div>
                      {!n.read && <div style={{ width:7, height:7, borderRadius:'50%', background:'#16a34a', flexShrink:0, marginTop:6 }} />}
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* MISSED ALERT BANNER */}
      {showAlerts && missedByPatient.length>0 && (
        <div style={{ background:'#FFF5F5', borderBottom:`2px solid ${C.errorBorder}`, padding:'.75rem 2rem' }}>
          <div style={{ maxWidth:1300, margin:'0 auto' }}>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'.5rem' }}>
              <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                <span style={{ fontSize:'1.1rem' }}>🚨</span>
                <span style={{ fontWeight:700, color:C.error, fontSize:'.85rem' }}>
                  {missedReminders.length} missed dose{missedReminders.length!==1?'s':''} today — {missedByPatient.length} patient{missedByPatient.length!==1?'s':''}
                </span>
              </div>
              <button onClick={()=>setShowAlerts(false)} style={{ background:'none', border:'none', color:C.muted, cursor:'pointer', fontSize:'1rem' }}>×</button>
            </div>
            <div style={{ display:'flex', gap:'.5rem', flexWrap:'wrap' }}>
              {missedByPatient.map(mp => {
                const patient = patients.find(p=>p.email===mp.email)
                return (
                  <div key={mp.email} style={{ background:'white', border:`1px solid ${C.errorBorder}`, borderRadius:8, padding:'.35rem .75rem', display:'flex', alignItems:'center', gap:8 }}>
                    <span style={{ fontSize:'.75rem', color:C.error, fontWeight:600 }}>❌ {mp.email}</span>
                    <span style={{ fontSize:'.68rem', color:C.muted }}>{mp.missed.length} missed</span>
                    {patient && (
                      <QuickRemind patient={patient} appt={patient.appointments[0]} fetchWithAuth={fetchWithAuth} onDone={loadAll} />
                    )}
                  </div>
                )
              })}
            </div>
          </div>
        </div>
      )}

      {/* MAIN TABS */}
      <div style={{ maxWidth:1300, margin:'0 auto', padding:'1rem 1.5rem 0' }}>
        <div style={{ display:'flex', gap:4, borderBottom:`1px solid ${C.border}`, marginBottom:'1rem' }}>
          {[['patients','👥 Patients'],['calendar','📅 Adherence Calendar'],['reminders','📤 Reminder History'],['voice','📞 Voice Agent'],['reviews','⭐ Reviews'],['staff','👤 Staff'],['pharmacies','💊 Pharmacies']].map(([key,label]) => (
            <button key={key} onClick={()=>setMainTab(key)}
              style={{ padding:'.6rem 1.2rem', fontSize:'.82rem', fontWeight:600, border:'none', cursor:'pointer', fontFamily:"'DM Sans',sans-serif", background:'none', borderBottom:mainTab===key?`2.5px solid ${C.g600}`:'2.5px solid transparent', color:mainTab===key?C.g700:C.muted, transition:'all .15s' }}>
              {label}
              {key==='reminders'&&reminderLogs.length>0&&<span style={{ marginLeft:6, background:C.g100, color:C.g600, borderRadius:999, padding:'.1rem .4rem', fontSize:'.65rem', fontWeight:700 }}>{reminderLogs.length}</span>}
            </button>
          ))}
        </div>
      </div>

      {/* PATIENTS TAB */}
      {mainTab==='patients' && (
        <div style={{ maxWidth:1300, margin:'0 auto', padding:'0 1.5rem 1.5rem', display:'grid', gridTemplateColumns:selectedPatient?'320px 1fr':'1fr', gap:'1.25rem', alignItems:'start' }}>

          {/* Patient List */}
          <div style={{ background:'white', borderRadius:14, border:`1px solid ${C.border}`, overflow:'hidden', boxShadow:'0 2px 10px rgba(13,34,24,.05)' }}>
            <div style={{ padding:'.85rem 1rem', borderBottom:`1px solid ${C.border}`, background:C.g50 }}>
              <input value={search} onChange={e=>setSearch(e.target.value)} placeholder="🔍  Search by name, email, phone…"
                style={{ width:'100%', padding:'.6rem .85rem', border:`1.5px solid ${C.border}`, borderRadius:9, fontSize:'.82rem', outline:'none', fontFamily:"'DM Sans',sans-serif", color:C.text, background:'white', boxSizing:'border-box', marginBottom:'.6rem' }} />
              <div style={{ display:'flex', gap:4, flexWrap:'wrap' }}>
                {['all','pending','confirmed','completed','cancelled'].map(s=>(
                  <button key={s} onClick={()=>setStatusFilter(s)}
                    style={{ padding:'.25rem .65rem', borderRadius:6, border:`1px solid ${statusFilter===s?C.g600:C.border}`, background:statusFilter===s?C.g700:'white', color:statusFilter===s?'white':C.muted, fontSize:'.68rem', fontWeight:600, cursor:'pointer', fontFamily:"'DM Sans',sans-serif", textTransform:'capitalize' }}>
                    {s==='all'?'All':s}
                  </button>
                ))}
              </div>
            </div>

            {loading ? (
              <div style={{ padding:'3rem', textAlign:'center', color:C.muted }}>
                <div style={{ fontSize:'1.5rem', opacity:.3 }}>⏳</div>
                <div style={{ fontSize:'.82rem', marginTop:8 }}>Loading patients…</div>
              </div>
            ) : filtered.length===0 ? (
              <div style={{ padding:'3rem', textAlign:'center', color:C.muted }}>
                <div style={{ fontSize:'2rem', opacity:.2 }}>👥</div>
                <div style={{ fontSize:'.82rem', marginTop:8 }}>No patients found</div>
              </div>
            ) : (
              <div style={{ overflowY:'auto', maxHeight:'calc(100vh - 280px)' }}>
                {filtered.map(p => {
                  const latest = p.appointments[0]
                  const sc = STATUS_COLORS[latest?.status]||STATUS_COLORS.pending
                  const isActive = selectedPatient?.email===p.email
                  const hasMed = p.appointments.some(a=>a.medication||a.prescription)
                  const pending = p.appointments.filter(a=>a.status==='pending').length
                  const patientMissed = missedReminders.filter(m=>m.patientEmail===p.email).length
                  return (
                    <div key={p.email||p.name} onClick={()=>{ setSelectedPatient(p); setActiveTab('overview') }}
                      style={{ padding:'.8rem 1rem', borderBottom:`1px solid ${C.border}`, cursor:'pointer', background:isActive?C.g100:'white', borderLeft:isActive?`3px solid ${C.g500}`:patientMissed>0?`3px solid ${C.error}`:'3px solid transparent', transition:'all .15s' }}
                      onMouseEnter={e=>{if(!isActive)e.currentTarget.style.background=C.g50}}
                      onMouseLeave={e=>{if(!isActive)e.currentTarget.style.background='white'}}>
                      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', gap:8 }}>
                        <div style={{ display:'flex', gap:9, alignItems:'flex-start', flex:1, minWidth:0 }}>
                          <div style={{ width:34, height:34, borderRadius:'50%', background:patientMissed>0?C.error:C.g700, color:'white', display:'flex', alignItems:'center', justifyContent:'center', fontFamily:"'DM Serif Display',serif", fontSize:'.95rem', flexShrink:0 }}>
                            {(p.name||'?')[0].toUpperCase()}
                          </div>
                          <div style={{ flex:1, minWidth:0 }}>
                            <div style={{ fontWeight:700, color:C.g900, fontSize:'.85rem', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{p.name||'Unknown'}</div>
                            <div style={{ fontSize:'.68rem', color:C.muted, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{p.email}</div>
                            <div style={{ display:'flex', gap:4, marginTop:'.25rem', flexWrap:'wrap' }}>
                              <span style={{ fontSize:'.6rem', background:C.g50, color:C.muted, border:`1px solid ${C.border}`, padding:'.1rem .35rem', borderRadius:4 }}>{p.appointments.length} appt{p.appointments.length>1?'s':''}</span>
                              {hasMed&&<span style={{ fontSize:'.6rem', background:'#FFF3E0', color:'#E65100', padding:'.1rem .35rem', borderRadius:4 }}>💊 On Meds</span>}
                              {pending>0&&<span style={{ fontSize:'.6rem', background:'#FEF9F0', color:'#B7770D', padding:'.1rem .35rem', borderRadius:4 }}>⏳ {pending} pending</span>}
                              {patientMissed>0&&<span style={{ fontSize:'.6rem', background:C.errorBg, color:C.error, border:`1px solid ${C.errorBorder}`, padding:'.1rem .35rem', borderRadius:4, fontWeight:700 }}>❌ {patientMissed} missed</span>}
                            </div>
                          </div>
                        </div>
                        <div style={{ textAlign:'right', flexShrink:0 }}>
                          <span style={{ fontSize:'.6rem', fontWeight:600, padding:'.15rem .45rem', borderRadius:999, background:sc.bg, color:sc.color, border:`1px solid ${sc.border}`, textTransform:'capitalize', display:'block', marginBottom:'.25rem' }}>{latest?.status||'—'}</span>
                          <div style={{ fontSize:'.6rem', color:C.muted }}>{fmtRelative(latest?.date)}</div>
                        </div>
                      </div>
                    </div>
                  )
                })}
              </div>
            )}
          </div>

          {/* Patient Detail */}
          {selectedPatient && (
            <div style={{ display:'flex', flexDirection:'column', gap:'1rem' }}>

              {/* Patient header */}
              <div style={{ background:`linear-gradient(135deg,${C.g800},${C.g700})`, borderRadius:14, padding:'1.25rem 1.5rem', boxShadow:'0 4px 16px rgba(13,34,24,.12)' }}>
                <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start' }}>
                  <div style={{ display:'flex', gap:12, alignItems:'center' }}>
                    <div style={{ width:48, height:48, borderRadius:'50%', background:'rgba(255,255,255,.15)', border:'2px solid rgba(255,255,255,.2)', display:'flex', alignItems:'center', justifyContent:'center', fontFamily:"'DM Serif Display',serif", fontSize:'1.4rem', color:'white', flexShrink:0 }}>
                      {(selectedPatient.name||'?')[0].toUpperCase()}
                    </div>
                    <div>
                      <div style={{ fontFamily:"'DM Serif Display',serif", fontSize:'1.3rem', color:'white' }}>{selectedPatient.name}</div>
                      <div style={{ fontSize:'.75rem', color:'rgba(255,255,255,.55)', marginTop:'.15rem' }}>{selectedPatient.email}</div>
                      {selectedPatient.phone&&<div style={{ fontSize:'.72rem', color:'rgba(255,255,255,.45)', marginTop:'.1rem' }}>📞 {selectedPatient.phone}</div>}
                    </div>
                  </div>
                  <button onClick={()=>setSelectedPatient(null)}
                    style={{ width:32, height:32, borderRadius:7, background:'rgba(255,255,255,.1)', border:'1px solid rgba(255,255,255,.2)', color:'rgba(255,255,255,.8)', cursor:'pointer', fontSize:'1.1rem', display:'flex', alignItems:'center', justifyContent:'center' }}>×</button>
                </div>

                {/* Quick stats */}
                <div style={{ display:'flex', gap:'.75rem', marginTop:'1rem', flexWrap:'wrap' }}>
                  {[
                    ['📋', selectedPatient.appointments.length, 'Total', false],
                    ['✅', selectedPatient.appointments.filter(a=>a.status==='completed').length, 'Completed', false],
                    ['⏳', selectedPatient.appointments.filter(a=>a.status==='pending').length, 'Pending', false],
                    ['💊', selectedPatient.appointments.filter(a=>a.medication||a.prescription).length, 'With Meds', false],
                    ['❌', missedReminders.filter(m=>m.patientEmail===selectedPatient.email).length, 'Missed Today', true],
                  ].map(([icon,val,label,isAlert]) => (
                    <div key={label} style={{ background:isAlert&&val>0?'rgba(192,57,43,.3)':'rgba(255,255,255,.1)', borderRadius:8, padding:'.4rem .75rem', display:'flex', alignItems:'center', gap:6 }}>
                      <span style={{ fontSize:'.85rem' }}>{icon}</span>
                      <div>
                        <div style={{ fontSize:'.95rem', fontWeight:700, color:isAlert&&val>0?'#FF9999':'white', lineHeight:1 }}>{val}</div>
                        <div style={{ fontSize:'.55rem', color:'rgba(255,255,255,.4)', textTransform:'uppercase', letterSpacing:'.07em', marginTop:1 }}>{label}</div>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Quick reminders */}
                <div style={{ marginTop:'1rem', paddingTop:'1rem', borderTop:'1px solid rgba(255,255,255,.1)' }}>
                  <div style={{ fontSize:'.6rem', color:'rgba(255,255,255,.4)', textTransform:'uppercase', letterSpacing:'.08em', marginBottom:'.5rem' }}>Quick Remind</div>
                  <QuickRemind patient={selectedPatient} appt={selectedPatient.appointments[0]} fetchWithAuth={fetchWithAuth} onDone={loadAll} />
                </div>

                {/* WhatsApp pref status */}
                <div style={{ marginTop:'1rem', paddingTop:'1rem', borderTop:'1px solid rgba(255,255,255,.1)', display:'flex', alignItems:'center', justifyContent:'space-between', gap:8 }}>
                  <div style={{ display:'flex', alignItems:'center', gap:7 }}>
                    <span style={{ fontSize:'.85rem' }}>💬</span>
                    <div>
                      <div style={{ fontSize:'.6rem', color:'rgba(255,255,255,.4)', textTransform:'uppercase', letterSpacing:'.07em' }}>WhatsApp Reminders</div>
                      {patientWaPref === null
                        ? <div style={{ fontSize:'.72rem', color:'rgba(255,255,255,.4)' }}>Loading…</div>
                        : <div style={{ display:'flex', alignItems:'center', gap:5, marginTop:1 }}>
                            <span style={{ fontSize:'.72rem', fontWeight:700, color: patientWaPref.enabled ? '#86efac' : '#fca5a5' }}>
                              {patientWaPref.enabled ? '✓ Enrolled' : '✗ Not enrolled'}
                            </span>
                            {patientWaPref.enabled && patientWaPref.phone &&
                              <span style={{ fontSize:'.65rem', color:'rgba(255,255,255,.45)' }}>· {patientWaPref.phone}</span>
                            }
                          </div>
                      }
                    </div>
                  </div>
                  {patientWaPref !== null && (
                    <button
                      disabled={waToggling}
                      onClick={async () => {
                        setWaToggling(true)
                        const phone = patientWaPref.phone || selectedPatient.phone || ''
                        const next = !patientWaPref.enabled
                        try {
                          const r = await fetchWithAuth(`${API_BASE}/api/admin/patient-wa-pref/${encodeURIComponent(selectedPatient.email)}`, {
                            method: 'POST', headers: { 'Content-Type': 'application/json' },
                            body: JSON.stringify({ enabled: next, phone })
                          })
                          if (r.ok) setPatientWaPref(p => ({ ...p, enabled: next, phone }))
                        } catch {}
                        setWaToggling(false)
                      }}
                      style={{ fontSize:'.65rem', padding:'.25rem .65rem', borderRadius:6, border:'1px solid rgba(255,255,255,.25)', background: patientWaPref.enabled ? 'rgba(220,38,38,.3)' : 'rgba(22,163,74,.3)', color:'white', cursor:'pointer', fontFamily:"'DM Sans',sans-serif", opacity: waToggling ? .5 : 1 }}>
                      {waToggling ? '…' : patientWaPref.enabled ? 'Disable' : 'Enable'}
                    </button>
                  )}
                </div>
              </div>

              {/* Missed doses alert */}
              {missedReminders.filter(m=>m.patientEmail===selectedPatient.email).length>0 && (
                <div style={{ background:C.errorBg, border:`1px solid ${C.errorBorder}`, borderRadius:12, padding:'1rem 1.25rem' }}>
                  <div style={{ fontWeight:700, color:C.error, fontSize:'.85rem', marginBottom:'.5rem' }}>
                    🚨 {missedReminders.filter(m=>m.patientEmail===selectedPatient.email).length} missed dose{missedReminders.filter(m=>m.patientEmail===selectedPatient.email).length!==1?'s':''} today
                  </div>
                  <div style={{ display:'flex', gap:'.4rem', flexWrap:'wrap' }}>
                    {missedReminders.filter(m=>m.patientEmail===selectedPatient.email).map((m,i)=>(
                      <span key={i} style={{ fontSize:'.72rem', background:'white', border:`1px solid ${C.errorBorder}`, borderRadius:6, padding:'.2rem .5rem', color:C.error }}>
                        {m.medicationType==='morning'?'🌅':m.medicationType==='afternoon'?'☀️':'🌙'} {m.medicationName} · {m.medicationType}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Detail tabs */}
              <div style={{ background:'white', borderRadius:14, border:`1px solid ${C.border}`, overflow:'hidden', boxShadow:'0 2px 10px rgba(13,34,24,.05)' }}>
                <div style={{ display:'flex', borderBottom:`1px solid ${C.border}`, background:C.g50 }}>
                  {[['overview','📊 Overview'],['appointments','📋 Appointments'],['adherence','💊 Adherence']].map(([key,label])=>(
                    <button key={key} onClick={()=>setActiveTab(key)}
                      style={{ flex:1, padding:'.65rem .5rem', fontSize:'.75rem', fontWeight:600, border:'none', cursor:'pointer', fontFamily:"'DM Sans',sans-serif", background:'none', borderBottom:activeTab===key?`2.5px solid ${C.g600}`:'2.5px solid transparent', color:activeTab===key?C.g700:C.muted, transition:'all .15s' }}>
                      {label}
                    </button>
                  ))}
                </div>
                <div style={{ padding:'1.25rem', overflowY:'auto', maxHeight:'calc(100vh - 460px)' }}>
                  {activeTab==='overview'     && <OverviewTab patient={selectedPatient} fetchWithAuth={fetchWithAuth} onDone={loadAll} />}
                  {activeTab==='appointments' && <AppointmentsTab patient={selectedPatient} fetchWithAuth={fetchWithAuth} onDone={loadAll} />}
                  {activeTab==='adherence'    && <AdherenceTab patient={selectedPatient} missedReminders={missedReminders.filter(m=>m.patientEmail===selectedPatient.email)} calendarData={calendarData.filter(()=>true)} fetchWithAuth={fetchWithAuth} onDone={loadAll} />}
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ADHERENCE CALENDAR TAB */}
      {mainTab==='calendar' && (
        <div style={{ maxWidth:1300, margin:'0 auto', padding:'0 1.5rem 1.5rem' }}>
          <div style={{ background:'white', borderRadius:14, border:`1px solid ${C.border}`, padding:'1.25rem', boxShadow:'0 2px 10px rgba(13,34,24,.05)' }}>
            <AdherenceCalendar calendarData={calendarData} selectedDate={selectedCalDate} onSelectDate={setSelectedCalDate} />

            {/* Patients who missed on selected date */}
            {selectedCalDate && (() => {
              const dayData = calendarData.find(c=>c.date===selectedCalDate)
              if (!dayData || dayData.missed===0) return null
              return (
                <div style={{ marginTop:'1rem', padding:'1rem', background:C.errorBg, border:`1px solid ${C.errorBorder}`, borderRadius:10 }}>
                  <div style={{ fontWeight:700, color:C.error, fontSize:'.82rem', marginBottom:'.5rem' }}>❌ {dayData.missed} missed doses on {fmtDate(selectedCalDate)}</div>
                  <div style={{ fontSize:'.75rem', color:C.muted }}>Go to Patients tab to send reminders to affected patients.</div>
                </div>
              )
            })()}
          </div>
        </div>
      )}

      {/* REMINDER HISTORY TAB */}
      {mainTab==='reminders' && (
        <div style={{ maxWidth:1300, margin:'0 auto', padding:'0 1.5rem 1.5rem' }}>
          <div style={{ background:'white', borderRadius:14, border:`1px solid ${C.border}`, overflow:'hidden', boxShadow:'0 2px 10px rgba(13,34,24,.05)' }}>
            <div style={{ padding:'1rem 1.25rem', borderBottom:`1px solid ${C.border}`, background:C.g50, display:'flex', justifyContent:'space-between', alignItems:'center' }}>
              <div style={{ fontWeight:700, color:C.g900, fontSize:'.88rem' }}>📤 Reminder History</div>
              <span style={{ fontSize:'.75rem', color:C.muted }}>{reminderLogs.length} total</span>
            </div>
            {reminderLogs.length===0 ? (
              <div style={{ padding:'4rem', textAlign:'center', color:C.muted }}>
                <div style={{ fontSize:'2.5rem', opacity:.2, marginBottom:'.75rem' }}>🔔</div>
                <div style={{ fontWeight:600, color:C.text, fontSize:'.9rem', marginBottom:'.4rem' }}>No reminders sent yet</div>
                <div style={{ fontSize:'.8rem' }}>Reminders will appear here once sent</div>
              </div>
            ) : (
              <div style={{ overflowY:'auto', maxHeight:'calc(100vh - 240px)' }}>
                {['manual','auto'].map(type => {
                  const logs = reminderLogs.filter(r=>type==='auto'?r.type==='auto':r.type!=='auto')
                  if (logs.length===0) return null
                  return (
                    <div key={type}>
                      <div style={{ padding:'.6rem 1.25rem', background:C.g50, borderBottom:`1px solid ${C.border}`, fontSize:'.65rem', fontWeight:700, color:C.muted, textTransform:'uppercase', letterSpacing:'.1em' }}>
                        {type==='auto'?'🤖 Automated (Cron)':'👤 Manual (Admin Sent)'}
                      </div>
                      {logs.map((log,i)=>(
                        <div key={log.id||i} style={{ padding:'.85rem 1.25rem', borderBottom:`1px solid ${C.border}`, display:'flex', justifyContent:'space-between', alignItems:'flex-start', gap:12 }}>
                          <div style={{ flex:1, minWidth:0 }}>
                            <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:'.3rem' }}>
                              <div style={{ width:28, height:28, borderRadius:'50%', background:C.g700, color:'white', display:'flex', alignItems:'center', justifyContent:'center', fontSize:'.75rem', flexShrink:0 }}>
                                {(log.name||log.sentTo||'?')[0].toUpperCase()}
                              </div>
                              <div>
                                <div style={{ fontWeight:600, color:C.g900, fontSize:'.82rem' }}>{log.name||'—'}</div>
                                <div style={{ fontSize:'.68rem', color:C.muted }}>{log.sentTo}</div>
                              </div>
                            </div>
                            {log.medicationName&&(
                              <span style={{ fontSize:'.65rem', background:'#FFF3E0', color:'#E65100', padding:'.1rem .4rem', borderRadius:4, display:'inline-block', marginBottom:'.3rem' }}>
                                {log.medicationType==='morning'?'🌅':log.medicationType==='afternoon'?'☀️':'🌙'} {log.medicationName} · {log.medicationType}
                              </span>
                            )}
                            {log.message&&(
                              <div style={{ fontSize:'.75rem', color:C.muted, lineHeight:1.6, background:C.g50, padding:'.4rem .6rem', borderRadius:6, border:`1px solid ${C.border}` }}>
                                {log.message}
                              </div>
                            )}
                          </div>
                          <div style={{ textAlign:'right', flexShrink:0 }}>
                            <div style={{ fontSize:'.65rem', color:C.muted }}>{fmtTime(log.sentAt)}</div>
                            <span style={{ fontSize:'.6rem', fontWeight:600, padding:'.1rem .4rem', borderRadius:4, background:type==='auto'?C.g100:'#EDF2FF', color:type==='auto'?C.g600:'#3451B2', display:'inline-block', marginTop:'.25rem' }}>
                              {type==='auto'?'🤖 Auto':'👤 Manual'}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )
                })}
              </div>
            )}
          </div>
        </div>
      )}

      {mainTab==='voice' && (
        <VoiceAgentTab fetchWithAuth={fetchWithAuth} />
      )}

      {mainTab==='reviews' && (
        <ReviewsTab fetchWithAuth={fetchWithAuth} />
      )}

      {mainTab==='staff' && (
        <StaffApplicationsTab fetchWithAuth={fetchWithAuth} />
      )}

      {mainTab==='pharmacies' && (
        <PharmaciesTab fetchWithAuth={fetchWithAuth} />
      )}

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Serif+Display:ital@0;1&family=DM+Sans:wght@300;400;500;600&display=swap');
        * { box-sizing:border-box; }
        ::-webkit-scrollbar{width:4px;height:4px}
        ::-webkit-scrollbar-track{background:transparent}
        ::-webkit-scrollbar-thumb{background:${C.border};border-radius:4px}
      `}</style>
    </div>
  )
}

// ─── Overview Tab ─────────────────────────────────────────────────────────────
function OverviewTab({ patient, fetchWithAuth, onDone }) {
  const latest = patient.appointments[0]
  const hasMed = patient.appointments.some(a=>a.medication||a.prescription)
  return (
    <div style={{ display:'flex', flexDirection:'column', gap:'.85rem' }}>
      <div style={{ background:C.g50, border:`1px solid ${C.border}`, borderRadius:10, padding:'1rem 1.1rem' }}>
        <div style={{ fontSize:'.65rem', fontWeight:700, color:C.muted, textTransform:'uppercase', letterSpacing:'.08em', marginBottom:'.75rem' }}>Treatment Status</div>
        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'.75rem' }}>
          {[['Last Visit',fmtDate(latest?.date)],['Service',latest?.service||'—'],['Status',latest?.status||'—'],['Mode',latest?.mode||'—'],['Follow-up',latest?.followUpDate?fmtDate(latest.followUpDate):'—'],['On Medication',hasMed?'✅ Yes':'❌ No']].map(([label,val])=>(
            <div key={label}>
              <div style={{ fontSize:'.62rem', fontWeight:600, color:C.muted, textTransform:'uppercase', letterSpacing:'.07em', marginBottom:'.2rem' }}>{label}</div>
              <div style={{ fontSize:'.85rem', color:C.text, fontWeight:500, textTransform:'capitalize' }}>{val}</div>
            </div>
          ))}
        </div>
      </div>
      {latest?.diagnosis&&<div style={{ background:'#EDF2FF', border:'1px solid #BAC8FF', borderRadius:10, padding:'1rem 1.1rem' }}><div style={{ fontSize:'.65rem', fontWeight:700, color:'#3451B2', textTransform:'uppercase', letterSpacing:'.08em', marginBottom:'.4rem' }}>🔬 Latest Diagnosis</div><div style={{ fontSize:'.85rem', color:'#1A2155', lineHeight:1.8, whiteSpace:'pre-wrap' }}>{latest.diagnosis}</div></div>}
      {(latest?.prescription||latest?.medication)&&<div style={{ background:'#F0FFF4', border:`1px solid ${C.g300}`, borderRadius:10, padding:'1rem 1.1rem' }}><div style={{ fontSize:'.65rem', fontWeight:700, color:C.g600, textTransform:'uppercase', letterSpacing:'.08em', marginBottom:'.4rem' }}>💊 Current Prescription</div><div style={{ fontSize:'.85rem', color:C.text, lineHeight:1.9, whiteSpace:'pre-wrap' }}>{latest?.prescription||latest?.medication}</div></div>}
    </div>
  )
}

// ─── Appointments Tab ─────────────────────────────────────────────────────────
function AppointmentsTab({ patient, fetchWithAuth, onDone }) {
  return (
    <div style={{ display:'flex', flexDirection:'column', gap:'.65rem' }}>
      {patient.appointments.map(a=>{
        const sc=STATUS_COLORS[a.status]||STATUS_COLORS.pending
        return (
          <div key={a.id} style={{ border:`1px solid ${C.border}`, borderRadius:10, padding:'.85rem 1rem', background:'white' }}>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', gap:8, marginBottom:'.5rem' }}>
              <div style={{ flex:1, minWidth:0 }}>
                <div style={{ fontWeight:700, color:C.g900, fontSize:'.88rem', marginBottom:'.2rem' }}>{a.service||'—'}</div>
                <div style={{ fontSize:'.72rem', color:C.muted, marginBottom:'.3rem' }}>📅 {fmtDate(a.date)}{a.timeSlot?` · ${a.timeSlot}`:''}{a.mode?` · ${a.mode}`:''}</div>
                <div style={{ display:'flex', gap:4, flexWrap:'wrap' }}>
                  {a.diagnosis&&<span style={{ fontSize:'.6rem', background:'#EDF2FF', color:'#3451B2', padding:'.1rem .35rem', borderRadius:4 }}>Dx ✓</span>}
                  {a.prescription&&<span style={{ fontSize:'.6rem', background:C.g100, color:C.g600, padding:'.1rem .35rem', borderRadius:4 }}>Rx ✓</span>}
                  {a.medication&&<span style={{ fontSize:'.6rem', background:'#FFF3E0', color:'#E65100', padding:'.1rem .35rem', borderRadius:4 }}>💊 Med</span>}
                  {a.followUpDate&&<span style={{ fontSize:'.6rem', background:C.g50, color:C.muted, border:`1px solid ${C.border}`, padding:'.1rem .35rem', borderRadius:4 }}>Follow-up: {fmtDate(a.followUpDate)}</span>}
                </div>
              </div>
              <span style={{ fontSize:'.62rem', fontWeight:600, padding:'.18rem .5rem', borderRadius:999, background:sc.bg, color:sc.color, border:`1px solid ${sc.border}`, textTransform:'capitalize', flexShrink:0 }}>{a.status}</span>
            </div>
            <QuickRemind patient={patient} appt={a} fetchWithAuth={fetchWithAuth} onDone={onDone} />
            {a.message&&<div style={{ marginTop:'.5rem', padding:'.5rem .75rem', background:C.g50, borderRadius:7, fontSize:'.78rem', color:C.muted, lineHeight:1.6 }}>💬 {a.message}</div>}
          </div>
        )
      })}
    </div>
  )
}

// ─── Adherence Tab ────────────────────────────────────────────────────────────
function AdherenceTab({ patient, missedReminders, fetchWithAuth, onDone }) {
  const todayStr = todayISO()
  const [adh, setAdh] = React.useState(null)   // real taken/not status from the patient's marks
  React.useEffect(() => {
    let alive = true
    fetchWithAuth(`${API_BASE}/api/admin/adherence?email=${encodeURIComponent(patient.email)}`)
      .then(r => r.ok ? r.json() : { patients: [] })
      .then(d => { if (alive) setAdh(d.patients || []) })
      .catch(() => { if (alive) setAdh([]) })
    return () => { alive = false }
  }, [patient.email, fetchWithAuth])
  const adhColor = p => p == null ? { background: '#f1f2f6', color: '#6b7280' } : p < 50 ? { background: '#FFF0F0', color: '#C0392B' } : p < 80 ? { background: '#FFF8E1', color: '#B7770D' } : { background: '#EBF8F0', color: '#1A6B3C' }
  const medAppointments = patient.appointments.filter(a=>a.medication||a.prescription)
  if (medAppointments.length===0) return (
    <div style={{ textAlign:'center', padding:'2.5rem', color:C.muted }}>
      <div style={{ fontSize:'2rem', opacity:.2, marginBottom:'.6rem' }}>💊</div>
      <div style={{ fontSize:'.88rem', fontWeight:600, color:C.text, marginBottom:'.4rem' }}>No medication prescribed yet</div>
    </div>
  )
  return (
    <div style={{ display:'flex', flexDirection:'column', gap:'1rem' }}>
      <div style={{ background:'#FFF8F0', border:'1px solid #FFCCAA', borderRadius:10, padding:'.85rem 1rem', fontSize:'.8rem', color:'#C25A00' }}>
        ℹ️ Adherence data is based on patient self-reporting via the patient dashboard.
      </div>

      {/* What the patient sees: real per-dose reminder status (taken / not reported) */}
      {adh && adh.map((row, ri) => (
        <div key={'adh'+ri} style={{ border:`1px solid ${C.border}`, borderRadius:12, overflow:'hidden' }}>
          <div style={{ background:C.g50, padding:'.7rem 1rem', borderBottom:`1px solid ${C.border}`, display:'flex', justifyContent:'space-between', alignItems:'center' }}>
            <div style={{ fontWeight:700, color:C.g900, fontSize:'.82rem' }}>💊 Reminders the patient sees — {row.service||'Treatment'}</div>
            <span style={{ fontSize:'.68rem', fontWeight:700, padding:'2px 10px', borderRadius:999, ...adhColor(row.pct) }}>{row.pct==null?'—':row.pct+'%'}</span>
          </div>
          <div style={{ padding:'.75rem 1rem', display:'flex', flexDirection:'column', gap:'.5rem' }}>
            {(row.perMed||[]).map((m,i)=>{
              const icon = m.type==='morning'?'🌅':m.type==='afternoon'?'☀️':'🌙'
              const slot = m.type==='morning'?'Morning':m.type==='afternoon'?'Afternoon':'Night'
              return (
                <div key={i} style={{ display:'flex', alignItems:'center', gap:10, flexWrap:'wrap' }}>
                  <span title={m.name} style={{ fontSize:'.74rem', width:200, whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>{icon} <b style={{fontWeight:600}}>{slot}</b> · {m.name}</span>
                  <div style={{ display:'flex', gap:3 }}>{(m.recent||[]).map(d=>{ const col=d.status==='taken'?'#22c55e':d.status==='missed'?'#C0392B':d.status==='pending'?'#F59E0B':'#e5e7eb'; return <span key={d.date} title={`${fmtDate(d.date)}: ${d.status==='none'?'no dose':d.status==='pending'?'due / not reported':d.status}`} style={{ width:13, height:13, borderRadius:3, background:col, display:'inline-block' }} /> })}</div>
                  <span style={{ fontSize:'.64rem', color:C.muted }}>✅ {m.taken} · ❌ {m.missed} · ⏳ {m.pending}</span>
                </div>
              )
            })}
            <div style={{ display:'flex', gap:12, fontSize:'.58rem', color:C.muted, marginTop:2, flexWrap:'wrap' }}>
              <span>🟢 taken</span><span>🔴 not reported</span><span>🟠 due today</span><span>⚪ no dose</span><span style={{ marginLeft:'auto' }}>last 7 days · old → today</span>
            </div>
          </div>
        </div>
      ))}

      {missedReminders?.length>0&&(
        <div style={{ background:C.errorBg, border:`1px solid ${C.errorBorder}`, borderRadius:10, padding:'.85rem 1rem' }}>
          <div style={{ fontWeight:700, color:C.error, fontSize:'.82rem', marginBottom:'.4rem' }}>❌ Missed today: {missedReminders.length} dose{missedReminders.length!==1?'s':''}</div>
          <div style={{ display:'flex', gap:4, flexWrap:'wrap', marginBottom:'.6rem' }}>
            {missedReminders.map((m,i)=><span key={i} style={{ fontSize:'.68rem', background:'white', border:`1px solid ${C.errorBorder}`, borderRadius:5, padding:'.15rem .45rem', color:C.error }}>{m.medicationType==='morning'?'🌅':m.medicationType==='afternoon'?'☀️':'🌙'} {m.medicationName}</span>)}
          </div>
          <QuickRemind patient={patient} appt={patient.appointments[0]} fetchWithAuth={fetchWithAuth} onDone={onDone} />
        </div>
      )}
      {medAppointments.map(a=>{
        const raw=a.date||todayStr
        const startDate=raw.includes('T')?raw.split('T')[0]:raw.split(' ')[0]
        const daysSince=Math.max(0,Math.floor((new Date()-new Date(startDate+'T00:00:00'))/86400000))
        const medLines=(a.medication||a.prescription||'').split('\n').filter(l=>/^\d+[\)\.]\s/.test(l.trim()))
        return (
          <div key={a.id} style={{ border:`1px solid ${C.border}`, borderRadius:12, overflow:'hidden' }}>
            <div style={{ background:C.g50, padding:'.75rem 1rem', borderBottom:`1px solid ${C.border}`, display:'flex', justifyContent:'space-between', alignItems:'center' }}>
              <div>
                <div style={{ fontWeight:700, color:C.g900, fontSize:'.85rem' }}>{a.service}</div>
                <div style={{ fontSize:'.68rem', color:C.muted }}>Started: {fmtDate(startDate)} · Day {daysSince+1}</div>
              </div>
            </div>
            <div style={{ padding:'.85rem 1rem' }}>
              {medLines.length>0?(
                <div style={{ display:'flex', flexDirection:'column', gap:'.5rem' }}>
                  {medLines.map((line,i)=>{
                    const dosage=line.match(/(\d)\s*[-–]\s*(\d)\s*[-–]\s*(\d)/)
                    const times=dosage?[dosage[1]!=='0'&&'🌅 Morning',dosage[2]!=='0'&&'☀️ Afternoon',dosage[3]!=='0'&&'🌙 Night'].filter(Boolean):[]
                    const dM=line.match(/(\d+)\s*day/),wM=line.match(/(\d+)\s*week/),mM=line.match(/(\d+)\s*month/)
                    const totalDays=mM?parseInt(mM[1])*30:wM?parseInt(wM[1])*7:parseInt(dM?.[1]||30)
                    const cleanName=line.replace(/^\d+[\)\.]\s*/,'').replace(/\s+\d\s*[-–]\s*\d\s*[-–]\s*\d.*/,'').trim()
                    const progress=Math.min(100,(daysSince/totalDays)*100)
                    return (
                      <div key={i} style={{ background:'#F9FAFB', borderRadius:8, padding:'.65rem .85rem', border:`1px solid ${C.border}` }}>
                        <div style={{ display:'flex', justifyContent:'space-between', marginBottom:'.4rem' }}>
                          <div style={{ fontWeight:600, color:C.text, fontSize:'.82rem' }}>{cleanName||line}</div>
                          <span style={{ fontSize:'.62rem', color:C.muted, background:'white', border:`1px solid ${C.border}`, padding:'.1rem .4rem', borderRadius:4 }}>{totalDays} days</span>
                        </div>
                        {times.length>0&&<div style={{ display:'flex', gap:4, marginBottom:'.4rem', flexWrap:'wrap' }}>{times.map(t=><span key={t} style={{ fontSize:'.62rem', background:C.g100, color:C.g700, padding:'.1rem .4rem', borderRadius:4, fontWeight:500 }}>{t}</span>)}</div>}
                        <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                          <div style={{ flex:1, height:5, background:C.border, borderRadius:999, overflow:'hidden' }}>
                            <div style={{ height:'100%', borderRadius:999, background:progress>75?C.g500:progress>40?C.g400:'#FCD34D', width:`${progress}%`, transition:'width .4s' }} />
                          </div>
                          <span style={{ fontSize:'.62rem', color:C.muted, whiteSpace:'nowrap' }}>Day {daysSince+1}/{totalDays}</span>
                        </div>
                      </div>
                    )
                  })}
                </div>
              ):(
                <div style={{ fontSize:'.82rem', color:C.muted, textAlign:'center', padding:'1rem' }}>No medication lines found.</div>
              )}
            </div>
          </div>
        )
      })}
    </div>
  )
}

// ─── Reviews Tab ─────────────────────────────────────────────────────────────
function ReviewsTab({ fetchWithAuth }) {
  const C = { g50:'#F0FFF4',g100:'#DCFCE7',g600:'#16A34A',g700:'#15803D',g900:'#14532D',border:'#E2E8F0',muted:'#64748B',text:'#1E293B' }
  const [reviews, setReviews] = React.useState([])
  const [loading, setLoading] = React.useState(true)
  const [filter, setFilter]   = React.useState('pending')

  const load = () => {
    setLoading(true)
    fetchWithAuth(`${API_BASE}/api/admin/reviews/pending`)
      .then(r => r.json()).then(data => { setReviews(Array.isArray(data) ? data : []); setLoading(false) }).catch(() => setLoading(false))
  }
  React.useEffect(load, [])

  const act = async (id, status) => {
    await fetchWithAuth(`${API_BASE}/api/admin/reviews/${id}`, { method:'PATCH', headers:{'Content-Type':'application/json'}, body: JSON.stringify({ status }) })
    load()
  }

  const filtered = reviews.filter(r => filter === 'all' ? true : r.status === filter)
  const pendingCount = reviews.filter(r => r.status === 'pending').length

  return (
    <div style={{ maxWidth:1000, margin:'0 auto', padding:'0 1.5rem 2rem' }}>
      <div style={{ display:'flex', gap:8, marginBottom:'1.25rem' }}>
        {[['pending','Pending'],['approved','Approved'],['rejected','Rejected'],['all','All']].map(([k,l]) => (
          <button key={k} onClick={()=>setFilter(k)}
            style={{ padding:'.4rem .9rem', fontSize:'.78rem', fontWeight:600, border:`1px solid ${filter===k?C.g600:C.border}`, borderRadius:20, cursor:'pointer', background: filter===k ? C.g600 : 'white', color: filter===k ? 'white' : C.muted, fontFamily:"'DM Sans',sans-serif" }}>
            {l}{k==='pending'&&pendingCount>0 ? ` (${pendingCount})` : ''}
          </button>
        ))}
      </div>

      {loading && <p style={{ color:C.muted, fontSize:'.85rem' }}>Loading...</p>}
      {!loading && filtered.length === 0 && <p style={{ color:C.muted, fontSize:'.85rem' }}>No {filter} reviews.</p>}

      <div style={{ display:'flex', flexDirection:'column', gap:'1rem' }}>
        {filtered.map(r => (
          <div key={r.id} style={{ background:'white', border:`1px solid ${r.status==='pending'?'#FCD34D':C.border}`, borderRadius:12, padding:'1.1rem 1.25rem', display:'flex', gap:'1rem', alignItems:'flex-start' }}>
            <div style={{ width:36, height:36, borderRadius:'50%', background:C.g700, color:'white', display:'flex', alignItems:'center', justifyContent:'center', fontSize:'.9rem', fontWeight:700, flexShrink:0 }}>{(r.name||'?')[0].toUpperCase()}</div>
            <div style={{ flex:1 }}>
              <div style={{ display:'flex', gap:'1rem', alignItems:'center', flexWrap:'wrap', marginBottom:4 }}>
                <span style={{ fontSize:'.85rem', fontWeight:600, color:C.g900 }}>{r.name||'Anonymous'}</span>
                <span style={{ color:'#F59E0B', fontSize:'.8rem' }}>{'★'.repeat(r.rating||0)}{'☆'.repeat(5-(r.rating||0))}</span>
                <span style={{ fontSize:'.72rem', color:C.muted }}>Product #{r.productId}</span>
                <span style={{ fontSize:'.72rem', color:C.muted }}>{new Date(r.createdAt).toLocaleDateString('en-IN')}</span>
                <span style={{ fontSize:'.7rem', fontWeight:700, padding:'.1rem .45rem', borderRadius:6,
                  background: r.status==='approved'?'#DCFCE7':r.status==='rejected'?'#FEE2E2':'#FEF9C3',
                  color: r.status==='approved'?C.g700:r.status==='rejected'?'#B91C1C':'#92400E' }}>
                  {r.status.toUpperCase()}
                </span>
              </div>
              <p style={{ fontSize:'.84rem', color:'#374151', lineHeight:1.7, margin:0 }}>{r.text}</p>
            </div>
            {r.status === 'pending' && (
              <div style={{ display:'flex', gap:6, flexShrink:0 }}>
                <button onClick={()=>act(r.id,'approved')}
                  style={{ padding:'.35rem .8rem', background:C.g600, color:'white', border:'none', borderRadius:7, fontSize:'.75rem', fontWeight:600, cursor:'pointer', fontFamily:"'DM Sans',sans-serif" }}>Approve</button>
                <button onClick={()=>act(r.id,'rejected')}
                  style={{ padding:'.35rem .8rem', background:'#FEE2E2', color:'#B91C1C', border:'1px solid #FECACA', borderRadius:7, fontSize:'.75rem', fontWeight:600, cursor:'pointer', fontFamily:"'DM Sans',sans-serif" }}>Reject</button>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  )
}

// ─── Voice Agent Tab ──────────────────────────────────────────────────────────
function VoiceAgentTab({ fetchWithAuth }) {
  const C = { g50:'#F0FFF4',g100:'#DCFCE7',g500:'#22C55E',g600:'#16A34A',g700:'#15803D',g900:'#14532D',border:'#E2E8F0',muted:'#64748B',text:'#1E293B',red:'#EF4444',orange:'#F59E0B' }
  const [followupPatients, setFollowupPatients] = React.useState([])
  const [inactivePatients, setInactivePatients] = React.useState([])
  const [logs, setLogs] = React.useState([])
  const [loading, setLoading] = React.useState(true)
  const [calling, setCalling] = React.useState(null)
  const [callResult, setCallResult] = React.useState(null)
  const [days, setDays] = React.useState(30)
  const [activeView, setActiveView] = React.useState('followup')

  const load = React.useCallback(async () => {
    setLoading(true)
    try {
      const [fr, ir, lr] = await Promise.all([
        fetchWithAuth(`${API_BASE}/api/voice/followup-patients`),
        fetchWithAuth(`${API_BASE}/api/voice/inactive-patients?days=${days}`),
        fetchWithAuth(`${API_BASE}/api/voice/ai-call-logs`)
      ])
      const fd = await fr.json(); const id = await ir.json(); const ld = await lr.json()
      setFollowupPatients(fd.patients || [])
      setInactivePatients(id.patients || [])
      setLogs(ld.logs || [])
    } catch(e) { console.error(e) }
    setLoading(false)
  }, [fetchWithAuth, days])

  React.useEffect(() => { load() }, [load])

  const callPatient = async (p) => {
    if (!p.phone) return alert('No phone number for this patient')
    const key = p.appointmentId || p.email
    setCalling(key); setCallResult(null)
    try {
      const r = await fetchWithAuth(`${API_BASE}/api/voice/followup-call`, {
        method:'POST', headers:{'Content-Type':'application/json'},
        body: JSON.stringify({
          appointmentId: p.appointmentId || null,
          name: p.name, phone: p.phone,
          diagnosis: p.diagnosis || '', medication: p.medication || ''
        })
      })
      const d = await r.json()
      if (d.ok) setCallResult({ type:'success', msg:`✅ Call started to ${p.name}! Ananya (Plivo AI) is calling now.` })
      else setCallResult({ type:'error', msg: d.message || 'Failed to start call' })
    } catch(e) { setCallResult({ type:'error', msg: e.message }) }
    setCalling(null)
    setTimeout(() => { load() }, 4000)
  }

  const isIssue = (log) => {
    const t = (log.transcript || '') + (log.summary || '')
    return /worse|pain|hurt|reaction|side.effect|rash|fever|swelling|not working|problem|issue|severe|burning/i.test(t)
  }

  const PatientRow = ({ p, keyField }) => (
    <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'.9rem 1.25rem', borderBottom:`1px solid ${C.border}`, gap:12 }}>
      <div style={{ display:'flex', alignItems:'center', gap:12 }}>
        <div style={{ width:36, height:36, borderRadius:'50%', background:C.g700, color:'white', display:'flex', alignItems:'center', justifyContent:'center', fontSize:'.85rem', fontWeight:700, flexShrink:0 }}>
          {(p.name||'?')[0].toUpperCase()}
        </div>
        <div>
          <div style={{ fontWeight:600, color:C.g900, fontSize:'.85rem' }}>{p.name||'Unknown'}</div>
          <div style={{ fontSize:'.72rem', color:C.muted }}>{p.email}</div>
          <div style={{ fontSize:'.72rem', color:C.muted, display:'flex', gap:8, marginTop:2 }}>
            {p.phone && <span>📱 {p.phone}</span>}
            {p.followUpDate && <span style={{ color: new Date(p.followUpDate) < new Date() ? C.red : C.orange }}>📅 Follow-up: {new Date(p.followUpDate).toLocaleDateString()}</span>}
            {p.diagnosis && <span>🩺 {p.diagnosis.slice(0,40)}</span>}
            {p.last_appointment && <span>Last visit: {new Date(p.last_appointment).toLocaleDateString()}</span>}
          </div>
        </div>
      </div>
      <button onClick={()=>callPatient(p)} disabled={calling===(p.appointmentId||p.email) || !p.phone}
        style={{ padding:'.5rem 1.1rem', borderRadius:8, border:'none', background: !p.phone?C.border:calling===(p.appointmentId||p.email)?C.g100:C.g600, color: !p.phone?C.muted:calling===(p.appointmentId||p.email)?C.g700:'white', fontSize:'.78rem', fontWeight:600, cursor: !p.phone?'not-allowed':'pointer', whiteSpace:'nowrap', flexShrink:0 }}>
        {calling===(p.appointmentId||p.email) ? '⏳ Calling...' : !p.phone ? 'No phone' : '📞 Call Now'}
      </button>
    </div>
  )

  return (
    <div style={{ maxWidth:1300, margin:'0 auto', padding:'0 1.5rem 1.5rem' }}>
      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:'1rem' }}>
        <div>
          <div style={{ fontWeight:700, color:C.g900, fontSize:'1rem' }}>📞 AI Voice Follow-up — Ananya</div>
          <div style={{ fontSize:'.75rem', color:C.muted, marginTop:2 }}>Plivo calls → ElevenLabs voice · Real conversation, not IVR</div>
        </div>
        <div style={{ display:'flex', gap:8 }}>
          {activeView==='inactive' && (
            <select value={days} onChange={e=>setDays(Number(e.target.value))}
              style={{ padding:'.4rem .7rem', borderRadius:8, border:`1px solid ${C.border}`, fontSize:'.8rem', color:C.text, cursor:'pointer' }}>
              <option value={14}>Inactive 14+ days</option>
              <option value={30}>Inactive 30+ days</option>
              <option value={60}>Inactive 60+ days</option>
              <option value={90}>Inactive 90+ days</option>
            </select>
          )}
          <button onClick={load} style={{ padding:'.4rem .9rem', borderRadius:8, border:`1px solid ${C.border}`, background:'white', fontSize:'.8rem', cursor:'pointer' }}>🔄 Refresh</button>
        </div>
      </div>

      {callResult && (
        <div style={{ padding:'.75rem 1rem', borderRadius:10, marginBottom:'1rem', background: callResult.type==='success'?C.g50:'#FEF2F2', border:`1px solid ${callResult.type==='success'?C.g500:C.red}`, color:callResult.type==='success'?C.g700:C.red, fontSize:'.82rem', fontWeight:500 }}>
          {callResult.msg}
        </div>
      )}

      <div style={{ display:'flex', gap:4, marginBottom:'1rem', borderBottom:`1px solid ${C.border}` }}>
        {[['followup','📅 Follow-up Due', followupPatients.length],['inactive','💤 Inactive', inactivePatients.length],['logs','📋 Call Logs', logs.length]].map(([k,l,cnt])=>(
          <button key={k} onClick={()=>setActiveView(k)} style={{ padding:'.5rem 1rem', border:'none', background:'none', cursor:'pointer', fontSize:'.82rem', fontWeight:600, borderBottom:activeView===k?`2px solid ${C.g600}`:'2px solid transparent', color:activeView===k?C.g700:C.muted }}>
            {l} {cnt>0&&<span style={{ background:C.g100, color:C.g700, borderRadius:999, padding:'.1rem .4rem', fontSize:'.65rem', marginLeft:4 }}>{cnt}</span>}
          </button>
        ))}
      </div>

      {loading ? (
        <div style={{ padding:'4rem', textAlign:'center', color:C.muted }}>Loading...</div>
      ) : activeView==='followup' ? (
        <div style={{ background:'white', borderRadius:14, border:`1px solid ${C.border}`, overflow:'hidden' }}>
          {followupPatients.length===0
            ? <div style={{ padding:'4rem', textAlign:'center', color:C.muted }}><div style={{ fontSize:'2rem', opacity:.2, marginBottom:'.5rem' }}>📅</div><div style={{ fontWeight:600 }}>No follow-ups due</div><div style={{ fontSize:'.8rem', marginTop:'.3rem' }}>Patients with upcoming followUpDate appear here</div></div>
            : followupPatients.map((p,i) => <PatientRow key={p.appointmentId||i} p={p} />)}
        </div>
      ) : activeView==='inactive' ? (
        <div style={{ background:'white', borderRadius:14, border:`1px solid ${C.border}`, overflow:'hidden' }}>
          {inactivePatients.length===0
            ? <div style={{ padding:'4rem', textAlign:'center', color:C.muted }}><div style={{ fontSize:'2rem', opacity:.2, marginBottom:'.5rem' }}>📞</div><div style={{ fontWeight:600 }}>No inactive patients</div></div>
            : inactivePatients.map((p,i) => <PatientRow key={p.email||i} p={p} />)}
        </div>
      ) : (
        <div style={{ background:'white', borderRadius:14, border:`1px solid ${C.border}`, overflow:'hidden' }}>
          {logs.length===0
            ? <div style={{ padding:'4rem', textAlign:'center', color:C.muted }}><div style={{ fontSize:'2rem', opacity:.2, marginBottom:'.5rem' }}>📋</div><div style={{ fontWeight:600 }}>No calls yet</div></div>
            : logs.map((log,i) => {
              const urgent = isIssue(log)
              return (
                <div key={log.id||i} style={{ padding:'1rem 1.25rem', borderBottom:`1px solid ${C.border}`, background: urgent ? '#FFF5F5' : 'white' }}>
                  <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:'.4rem' }}>
                    <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                      {urgent && <span style={{ fontSize:'1rem' }}>🚨</span>}
                      <span style={{ fontWeight:700, color: urgent ? C.red : C.g900, fontSize:'.85rem' }}>{log.patient_name||'Unknown'}</span>
                      <span style={{ fontSize:'.72rem', color:C.muted }}>📱 {log.phone}</span>
                    </div>
                    <div style={{ display:'flex', gap:6, alignItems:'center', flexShrink:0 }}>
                      {log.wants_booking===1 && <span style={{ fontSize:'.65rem', background:C.g100, color:C.g700, padding:'.2rem .5rem', borderRadius:999, fontWeight:700 }}>📅 Wants Booking</span>}
                      {log.symptoms_ok===1 && <span style={{ fontSize:'.65rem', background:'#EFF6FF', color:'#1D4ED8', padding:'.2rem .5rem', borderRadius:999 }}>✓ Improving</span>}
                      {log.meds_ok===1 && <span style={{ fontSize:'.65rem', background:'#F0FDF4', color:C.g700, padding:'.2rem .5rem', borderRadius:999 }}>✓ Taking meds</span>}
                      <span style={{ fontSize:'.65rem', background:log.status==='completed'?C.g100:'#FEF3C7', color:log.status==='completed'?C.g700:'#92400E', padding:'.2rem .5rem', borderRadius:999, fontWeight:600 }}>
                        {log.status==='completed'?'✅ Done':'⏳ '+log.status}
                      </span>
                      <span style={{ fontSize:'.65rem', color:C.muted }}>{log.createdAt ? new Date(log.createdAt).toLocaleString('en-IN') : ''}</span>
                    </div>
                  </div>
                  {log.summary && <div style={{ fontSize:'.78rem', color: urgent ? '#7F1D1D' : C.muted, background: urgent ? '#FEE2E2' : C.g50, padding:'.5rem .75rem', borderRadius:8, marginBottom:'.4rem' }}>{urgent ? '⚠️ ' : '📝 '}{log.summary}</div>}
                  {log.transcript && (
                    <details style={{ fontSize:'.72rem', color:C.muted }}>
                      <summary style={{ cursor:'pointer', fontWeight:600, color:C.g700 }}>View full transcript</summary>
                      <pre style={{ whiteSpace:'pre-wrap', marginTop:'.5rem', background:'#F8FAFC', padding:'.75rem', borderRadius:8, lineHeight:1.7 }}>{log.transcript}</pre>
                    </details>
                  )}
                </div>
              )
            })
          }
        </div>
      )}
    </div>
  )
}

// ─── Staff Applications Tab ───────────────────────────────────────────────────
const ROLE_FIELDS = {
  doctor:  [{ key:'qualification', label:'Qualification (e.g. MBBS, MD)' }, { key:'specialization', label:'Specialization' }, { key:'experience', label:'Years of Experience' }],
  manager: [{ key:'previousClinic', label:'Previous Clinic' }, { key:'managementExperience', label:'Management Experience' }],
  kt:      [{ key:'certification', label:'Certification / Training' }, { key:'treatments', label:'Treatments Specialized In' }, { key:'experience', label:'Years of Experience' }],
}

function AddStaffModal({ fetchWithAuth, onDone, onClose }) {
  const [form, setForm]     = React.useState({ role:'doctor', name:'', email:'', phone:'' })
  const [saving, setSaving] = React.useState(false)
  const [msg, setMsg]       = React.useState('')
  const set = (k,v) => setForm(f => ({ ...f, [k]: v }))

  const submit = async () => {
    if (!form.name || !form.email || !form.phone) { setMsg('Name, email and phone are required'); return }
    setSaving(true); setMsg('')
    try {
      const r = await fetchWithAuth(`${API_BASE}/api/staff/invite`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form)
      })
      const d = await r.json()
      if (r.ok) { onDone(d.waSent); onClose() }
      else setMsg(d.message || 'Error adding staff')
    } catch(e) { setMsg(e.message) }
    finally { setSaving(false) }
  }

  const inp = (key, label, type='text') => (
    <div key={key} style={{ marginBottom: '.75rem' }}>
      <label style={{ fontSize: '.75rem', fontWeight: 600, color: C.muted, display: 'block', marginBottom: '.25rem' }}>{label}</label>
      <input type={type} value={form[key]||''} onChange={e => set(key, e.target.value)}
        style={{ width: '100%', padding: '.65rem .9rem', border: `1.5px solid ${C.border}`, borderRadius: 8, fontSize: '.85rem', outline: 'none', fontFamily: "'DM Sans',sans-serif", boxSizing: 'border-box' }} />
    </div>
  )

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.45)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem' }}>
      <div style={{ background: 'white', borderRadius: 18, padding: '1.75rem', width: '100%', maxWidth: 440, boxShadow: '0 8px 40px rgba(0,0,0,.18)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.25rem' }}>
          <div style={{ fontFamily: "'DM Serif Display',serif", fontSize: '1.2rem', color: C.g900 }}>Add Staff Member</div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: '1.2rem', cursor: 'pointer', color: C.muted }}>✕</button>
        </div>

        {/* Role selector */}
        <div style={{ marginBottom: '.85rem' }}>
          <label style={{ fontSize: '.75rem', fontWeight: 600, color: C.muted, display: 'block', marginBottom: '.25rem' }}>Role</label>
          <div style={{ display: 'flex', gap: '.5rem' }}>
            {[['doctor','🩺 Doctor'],['manager','🏥 Manager'],['kt','✨ KT']].map(([v,l]) => (
              <button key={v} onClick={() => set('role', v)}
                style={{ flex: 1, padding: '.55rem', borderRadius: 8, border: `1.5px solid ${form.role===v ? C.g600 : C.border}`, background: form.role===v ? C.g700 : 'white', color: form.role===v ? 'white' : C.muted, fontSize: '.78rem', fontWeight: 600, cursor: 'pointer', fontFamily: "'DM Sans',sans-serif" }}>
                {l}
              </button>
            ))}
          </div>
        </div>

        {inp('name',  'Full Name')}
        {inp('email', 'Email Address', 'email')}
        {inp('phone', 'Phone (for WhatsApp)')}
        {(ROLE_FIELDS[form.role]||[]).map(f => inp(f.key, f.label))}

        {msg && <div style={{ fontSize: '.78rem', color: '#991B1B', background: '#FEE2E2', borderRadius: 8, padding: '.6rem .9rem', marginBottom: '.75rem' }}>{msg}</div>}

        <div style={{ display: 'flex', gap: '.6rem', marginTop: '.25rem' }}>
          <button onClick={onClose} style={{ flex: 1, padding: '.75rem', borderRadius: 9, border: `1px solid ${C.border}`, background: 'white', color: C.muted, fontSize: '.85rem', cursor: 'pointer', fontFamily: "'DM Sans',sans-serif" }}>Cancel</button>
          <button onClick={submit} disabled={saving}
            style={{ flex: 2, padding: '.75rem', borderRadius: 9, border: 'none', background: saving ? C.g400 : C.g700, color: 'white', fontSize: '.85rem', fontWeight: 600, cursor: saving ? 'wait' : 'pointer', fontFamily: "'DM Sans',sans-serif" }}>
            {saving ? 'Adding…' : 'Add & Send WhatsApp'}
          </button>
        </div>
      </div>
    </div>
  )
}

function StaffApplicationsTab({ fetchWithAuth }) {
  const [apps, setApps]         = React.useState([])
  const [loading, setLoading]   = React.useState(true)
  const [filter, setFilter]     = React.useState('approved')
  const [acting, setActing]     = React.useState(null)
  const [showModal, setShowModal] = React.useState(false)

  const load = React.useCallback(async () => {
    setLoading(true)
    try {
      const r = await fetchWithAuth(`${API_BASE}/api/staff/applications?status=${filter}`)
      const d = await r.json()
      setApps(d.applications || [])
    } catch(e) { console.error(e) }
    finally { setLoading(false) }
  }, [filter, fetchWithAuth])

  React.useEffect(() => { load() }, [load])

  const act = async (id, action) => {
    setActing(id + action)
    try {
      await fetchWithAuth(`${API_BASE}/api/staff/${id}/${action}`, { method: 'POST' })
      load()
    } catch(e) { alert('Error: ' + e.message) }
    finally { setActing(null) }
  }

  const ROLE_LABEL   = { doctor: '🩺 Doctor', manager: '🏥 Manager', kt: '✨ Skin Therapist (KT)' }
  const STATUS_COLOR = { pending: '#B45309', approved: '#166534', rejected: '#991B1B' }
  const STATUS_BG    = { pending: '#FEF3C7', approved: '#DCFCE7', rejected: '#FEE2E2' }

  return (
    <div style={{ maxWidth: 900, margin: '0 auto', padding: '1rem 1.5rem' }}>
      {showModal && (
        <AddStaffModal
          fetchWithAuth={fetchWithAuth}
          onClose={() => setShowModal(false)}
          onDone={(waSent) => { load(); alert(waSent ? '✅ Staff added and WhatsApp sent!' : '✅ Staff added. (WhatsApp not configured)') }}
        />
      )}
      <div style={{ display: 'flex', gap: 6, marginBottom: '1.25rem', alignItems: 'center' }}>
        {['approved','pending','rejected'].map(s => (
          <button key={s} onClick={() => setFilter(s)}
            style={{ padding: '.45rem 1rem', borderRadius: 8, border: `1.5px solid ${filter===s ? C.g600 : C.border}`, background: filter===s ? C.g700 : 'white', color: filter===s ? 'white' : C.muted, fontSize: '.78rem', fontWeight: 600, cursor: 'pointer', fontFamily: "'DM Sans',sans-serif", textTransform: 'capitalize' }}>
            {s}
          </button>
        ))}
        <button onClick={load} style={{ padding: '.45rem .85rem', borderRadius: 8, border: `1px solid ${C.border}`, background: 'white', color: C.muted, fontSize: '.78rem', cursor: 'pointer', fontFamily: "'DM Sans',sans-serif" }}>
          Refresh
        </button>
        <button onClick={() => setShowModal(true)}
          style={{ marginLeft: 'auto', padding: '.45rem 1.1rem', borderRadius: 8, border: 'none', background: C.g700, color: 'white', fontSize: '.78rem', fontWeight: 600, cursor: 'pointer', fontFamily: "'DM Sans',sans-serif" }}>
          + Add Staff
        </button>
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: '3rem', color: C.muted, fontSize: '.85rem' }}>Loading…</div>
      ) : apps.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '3rem', color: C.muted, fontSize: '.85rem' }}>No {filter} applications.</div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '.85rem' }}>
          {apps.map(a => (
            <div key={a.id} style={{ background: 'white', border: `1px solid ${C.border}`, borderRadius: 14, padding: '1.1rem 1.25rem', boxShadow: '0 1px 4px rgba(13,34,24,.05)' }}>
              <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '1rem', flexWrap: 'wrap' }}>
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '.6rem', marginBottom: '.4rem', flexWrap: 'wrap' }}>
                    <span style={{ fontWeight: 700, fontSize: '.95rem', color: C.g900 }}>{a.name}</span>
                    <span style={{ fontSize: '.68rem', fontWeight: 700, background: STATUS_BG[a.status], color: STATUS_COLOR[a.status], padding: '.15rem .55rem', borderRadius: 999, textTransform: 'uppercase', letterSpacing: '.05em' }}>{a.status}</span>
                    <span style={{ fontSize: '.72rem', color: C.muted }}>{ROLE_LABEL[a.role] || a.role}</span>
                  </div>
                  <div style={{ fontSize: '.78rem', color: C.muted, display: 'flex', gap: '1.25rem', flexWrap: 'wrap', marginBottom: '.5rem' }}>
                    <span>✉️ {a.email}</span>
                    <span>📱 {a.phone}</span>
                    <span style={{ fontSize: '.72rem' }}>Applied {new Date(a.createdAt).toLocaleDateString('en-IN')}</span>
                  </div>
                  <div style={{ display: 'flex', gap: '.5rem', flexWrap: 'wrap' }}>
                    {a.qualification  && <span style={{ fontSize: '.72rem', background: C.g50, color: C.g700, border: `1px solid ${C.border}`, borderRadius: 6, padding: '.15rem .5rem' }}>🎓 {a.qualification}</span>}
                    {a.specialization && <span style={{ fontSize: '.72rem', background: C.g50, color: C.g700, border: `1px solid ${C.border}`, borderRadius: 6, padding: '.15rem .5rem' }}>⚕️ {a.specialization}</span>}
                    {a.experience     && <span style={{ fontSize: '.72rem', background: C.g50, color: C.g700, border: `1px solid ${C.border}`, borderRadius: 6, padding: '.15rem .5rem' }}>📅 {a.experience} yrs exp</span>}
                    {a.certification  && <span style={{ fontSize: '.72rem', background: C.g50, color: C.g700, border: `1px solid ${C.border}`, borderRadius: 6, padding: '.15rem .5rem' }}>📜 {a.certification}</span>}
                    {a.treatments     && <span style={{ fontSize: '.72rem', background: C.g50, color: C.g700, border: `1px solid ${C.border}`, borderRadius: 6, padding: '.15rem .5rem' }}>✨ {a.treatments}</span>}
                    {a.previousClinic && <span style={{ fontSize: '.72rem', background: C.g50, color: C.g700, border: `1px solid ${C.border}`, borderRadius: 6, padding: '.15rem .5rem' }}>🏥 {a.previousClinic}</span>}
                  </div>
                </div>
                {filter === 'pending' && (
                  <div style={{ display: 'flex', gap: '.5rem', flexShrink: 0 }}>
                    <button onClick={() => act(a.id, 'approve')} disabled={!!acting}
                      style={{ padding: '.5rem 1.1rem', borderRadius: 8, background: acting === a.id+'approve' ? C.g400 : C.g700, color: 'white', border: 'none', fontSize: '.78rem', fontWeight: 600, cursor: acting ? 'wait' : 'pointer', fontFamily: "'DM Sans',sans-serif" }}>
                      {acting === a.id+'approve' ? '…' : '✅ Approve'}
                    </button>
                    <button onClick={() => act(a.id, 'reject')} disabled={!!acting}
                      style={{ padding: '.5rem 1.1rem', borderRadius: 8, background: 'white', color: '#991B1B', border: '1.5px solid #FCA5A5', fontSize: '.78rem', fontWeight: 600, cursor: acting ? 'wait' : 'pointer', fontFamily: "'DM Sans',sans-serif" }}>
                      {acting === a.id+'reject' ? '…' : '❌ Reject'}
                    </button>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

// ─── Pharmacies Tab ───────────────────────────────────────────────────────────
function PharmaciesTab({ fetchWithAuth }) {
  const [pharmacies, setPharmacies] = React.useState([])
  const [loading, setLoading]       = React.useState(true)
  const [filter, setFilter]         = React.useState('pending')
  const [acting, setActing]         = React.useState(null)

  const load = React.useCallback(async () => {
    setLoading(true)
    try {
      const r = await fetchWithAuth(`${API_BASE}/api/pharmacies?status=${filter}`)
      const d = await r.json()
      setPharmacies(d.pharmacies || [])
    } catch(e) { console.error(e) }
    finally { setLoading(false) }
  }, [filter, fetchWithAuth])

  React.useEffect(() => { load() }, [load])

  const act = async (id, action) => {
    setActing(id + action)
    try {
      await fetchWithAuth(`${API_BASE}/api/pharmacy/${id}/${action}`, { method: 'POST' })
      load()
    } catch(e) { alert('Error: ' + e.message) }
    finally { setActing(null) }
  }

  const STATUS_COLOR = { pending: '#B45309', approved: '#166534', rejected: '#991B1B' }
  const STATUS_BG    = { pending: '#FEF3C7', approved: '#DCFCE7', rejected: '#FEE2E2' }

  return (
    <div style={{ maxWidth: 900, margin: '0 auto', padding: '1rem 1.5rem' }}>
      <div style={{ display: 'flex', gap: 6, marginBottom: '1.25rem', alignItems: 'center' }}>
        {['pending','approved','rejected'].map(s => (
          <button key={s} onClick={() => setFilter(s)}
            style={{ padding: '.45rem 1rem', borderRadius: 8, border: `1.5px solid ${filter===s ? C.g600 : C.border}`, background: filter===s ? C.g700 : 'white', color: filter===s ? 'white' : C.muted, fontSize: '.78rem', fontWeight: 600, cursor: 'pointer', fontFamily: "'DM Sans',sans-serif", textTransform: 'capitalize' }}>
            {s}
          </button>
        ))}
        <button onClick={load} style={{ padding: '.45rem .85rem', borderRadius: 8, border: `1px solid ${C.border}`, background: 'white', color: C.muted, fontSize: '.78rem', cursor: 'pointer', fontFamily: "'DM Sans',sans-serif" }}>Refresh</button>
        <a href="/pharmacy-register" target="_blank" rel="noreferrer"
          style={{ marginLeft: 'auto', padding: '.45rem 1rem', borderRadius: 8, border: `1px solid ${C.border}`, background: 'white', color: C.g700, fontSize: '.78rem', fontWeight: 600, textDecoration: 'none' }}>
          Share Registration Link ↗
        </a>
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: '3rem', color: C.muted, fontSize: '.85rem' }}>Loading…</div>
      ) : pharmacies.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '3rem', color: C.muted, fontSize: '.85rem' }}>
          No {filter} pharmacy applications.<br />
          <span style={{ fontSize: '.78rem' }}>Share the registration link with pharmacies to get started.</span>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '.85rem' }}>
          {pharmacies.map(p => (
            <div key={p.id} style={{ background: 'white', border: `1px solid ${C.border}`, borderRadius: 14, padding: '1.1rem 1.25rem', boxShadow: '0 1px 4px rgba(13,34,24,.05)' }}>
              <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '1rem', flexWrap: 'wrap' }}>
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '.6rem', marginBottom: '.4rem', flexWrap: 'wrap' }}>
                    <span style={{ fontWeight: 700, fontSize: '.95rem', color: C.g900 }}>💊 {p.name}</span>
                    <span style={{ fontSize: '.68rem', fontWeight: 700, background: STATUS_BG[p.status], color: STATUS_COLOR[p.status], padding: '.15rem .55rem', borderRadius: 999, textTransform: 'uppercase' }}>{p.status}</span>
                  </div>
                  <div style={{ fontSize: '.78rem', color: C.muted, display: 'flex', gap: '1.25rem', flexWrap: 'wrap', marginBottom: '.4rem' }}>
                    {p.ownerName && <span>👤 {p.ownerName}</span>}
                    <span>📱 {p.phone}</span>
                    {p.email && <span>✉️ {p.email}</span>}
                  </div>
                  <div style={{ display: 'flex', gap: '.5rem', flexWrap: 'wrap' }}>
                    {p.address       && <span style={{ fontSize: '.72rem', background: C.g50, color: C.g700, border: `1px solid ${C.border}`, borderRadius: 6, padding: '.15rem .5rem' }}>📍 {p.address}</span>}
                    {p.licenseNumber && <span style={{ fontSize: '.72rem', background: C.g50, color: C.g700, border: `1px solid ${C.border}`, borderRadius: 6, padding: '.15rem .5rem' }}>📜 {p.licenseNumber}</span>}
                    <span style={{ fontSize: '.72rem', color: C.muted }}>Applied {new Date(p.createdAt).toLocaleDateString('en-IN')}</span>
                  </div>
                </div>
                {filter === 'pending' && (
                  <div style={{ display: 'flex', gap: '.5rem', flexShrink: 0 }}>
                    <button onClick={() => act(p.id, 'approve')} disabled={!!acting}
                      style={{ padding: '.5rem 1.1rem', borderRadius: 8, background: acting===p.id+'approve' ? C.g400 : C.g700, color: 'white', border: 'none', fontSize: '.78rem', fontWeight: 600, cursor: acting ? 'wait' : 'pointer', fontFamily: "'DM Sans',sans-serif" }}>
                      {acting===p.id+'approve' ? '…' : '✅ Approve'}
                    </button>
                    <button onClick={() => act(p.id, 'reject')} disabled={!!acting}
                      style={{ padding: '.5rem 1.1rem', borderRadius: 8, background: 'white', color: '#991B1B', border: '1.5px solid #FCA5A5', fontSize: '.78rem', fontWeight: 600, cursor: acting ? 'wait' : 'pointer', fontFamily: "'DM Sans',sans-serif" }}>
                      {acting===p.id+'reject' ? '…' : '❌ Reject'}
                    </button>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
